<script setup lang="ts">
import type { MeOrgPosition } from '~/composables/equipe/useMyOrgPositions'
import type { OnboardingTrainingItem } from '~/composables/onboarding/useOnboarding'
import type { SeasonalDecorPrefs, SeasonalEventConfig } from '~/composables/ui/useSeasonalTheme'
import {
  ASYNC_OVERLAY_DELAY_MS,
  defineAsyncWithSkeleton,
} from '~/utils/defineAsyncWithSkeleton'

const GeolocateOrganigrammeModals = defineAsyncWithSkeleton(
  function loadGeolocateOrganigrammeModals() {
    return import('~/components/orga/geolocate/GeolocateOrganigrammeModals.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

const AppNoticeModal = defineAsyncWithSkeleton(
  function loadAppNoticeModal() {
    return import('~/components/app/AppNoticeModal.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

const SeasonalWelcomeModal = defineAsyncWithSkeleton(
  function loadSeasonalWelcomeModal() {
    return import('~/components/app/SeasonalWelcomeModal.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

const OnboardingTrainingCenterModal = defineAsyncWithSkeleton(
  function loadOnboardingTrainingCenterModal() {
    return import('~/components/app/OnboardingTrainingCenterModal.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

const AvatarModeGateModal = defineAsyncWithSkeleton(
  function loadAvatarModeGateModal() {
    return import('~/components/orga/avatar/AvatarModeGateModal.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

const OrgAccessOrientationModals = defineAsyncWithSkeleton(
  function loadOrgAccessOrientationModals() {
    return import('~/components/orga/OrgAccessOrientationModals.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

defineProps<{
  showGeolocateEmptyModal: boolean
  showGeolocateErrorModal: boolean
  showGeolocateChoiceModal: boolean
  geolocateChoices: MeOrgPosition[]
  geolocateErrorMessage: string
  showBetaNotice: boolean
  showSeasonalWelcome: boolean
  activeSeasonalEvent: SeasonalEventConfig | null
  decorPrefs: SeasonalDecorPrefs
  showTrainingCenter: boolean
  availableTrainings: OnboardingTrainingItem[]
  launchBlockerReason: string | null
  shouldShowAvatarModeGate: boolean
  isSavingAvatarMode: boolean
  fullName: string
  myGraphPhotoUrl: string | null
}>()

const emit = defineEmits<{
  'update:showGeolocateEmpty': [value: boolean]
  'update:showGeolocateError': [value: boolean]
  'update:showGeolocateChoice': [value: boolean]
  closeGeolocateChoice: []
  geolocateChoicePick: [choice: MeOrgPosition]
  acknowledgeBetaNotice: []
  'update:showSeasonalWelcome': [value: boolean]
  seasonalWelcomeConfirm: [payload: { suppressUntilNextEvent: boolean; prefs: SeasonalDecorPrefs }]
  liveSeasonalDecorUpdates: [prefs: { cursor: boolean; corner: boolean; logo: boolean }]
  'update:showTrainingCenter': [value: boolean]
  startTraining: [payload: { tourId: string; mode: 'do' | 'redo' }]
  confirmAvatarMode: [payload: { avatarDisplayMode: 'teams' | 'avatar' | 'initials'; avatarConfig: Record<string, unknown> | null }]
}>()
</script>

<template>
  <GeolocateOrganigrammeModals
    v-if="showGeolocateEmptyModal || showGeolocateErrorModal || showGeolocateChoiceModal"
    :show-empty="showGeolocateEmptyModal"
    :show-error="showGeolocateErrorModal"
    :show-choice="showGeolocateChoiceModal"
    :choices="geolocateChoices"
    :error-message="geolocateErrorMessage"
    @update:show-empty="emit('update:showGeolocateEmpty', $event)"
    @update:show-error="emit('update:showGeolocateError', $event)"
    @update:show-choice="emit('update:showGeolocateChoice', $event)"
    @close-choice="emit('closeGeolocateChoice')"
    @pick="emit('geolocateChoicePick', $event)"
  />
  <AppNoticeModal
    v-if="showBetaNotice"
    :model-value="showBetaNotice"
    title="Environnement de développement / recette"
    @confirm="emit('acknowledgeBetaNotice')"
  >
    <p>
      Cet environnement correspond à une version de développement (DEV). À ce titre, il est susceptible de contenir des données incomplètes, fictives ou non représentatives de la réalité, ainsi que des fonctionnalités en cours d’implémentation ou de validation. Des anomalies, comportements instables ou interruptions de service peuvent survenir.
    </p>
    <p>
      L’organigramme présenté dans cette application est fourni uniquement à des fins de démonstration. Il ne reflète en aucun cas l’organigramme réel de l’organisation. Les informations affichées sont simulées et ne doivent pas être interprétées comme des données officielles.
    </p>
    <p>
      Toute modification de l’organigramme ne relève pas de la responsabilité du développeur. La gestion, la validation et la mise à jour de ces données sont exclusivement du ressort de l’administrateur de l’application.
    </p>
    <p>
      Enfin, cette version est une version bêta. Elle est mise à disposition dans le but de tester, évaluer et améliorer les fonctionnalités avant une éventuelle mise en production. Des évolutions majeures peuvent encore intervenir sans préavis.
    </p>
  </AppNoticeModal>
  <SeasonalWelcomeModal
    v-if="showSeasonalWelcome"
    :model-value="showSeasonalWelcome"
    :event="activeSeasonalEvent"
    :decor-prefs="decorPrefs"
    @update:model-value="emit('update:showSeasonalWelcome', $event)"
    @confirm="emit('seasonalWelcomeConfirm', $event)"
    @live-prefs="emit('liveSeasonalDecorUpdates', $event)"
  />
  <OnboardingTrainingCenterModal
    v-if="showTrainingCenter"
    :model-value="showTrainingCenter"
    :trainings="availableTrainings"
    :blocker-reason="launchBlockerReason"
    @update:model-value="emit('update:showTrainingCenter', $event)"
    @start-training="emit('startTraining', $event)"
  />
  <AvatarModeGateModal
    v-if="shouldShowAvatarModeGate"
    :show="shouldShowAvatarModeGate"
    :saving="isSavingAvatarMode"
    :full-name="fullName"
    :photo-url="myGraphPhotoUrl"
    @confirm="emit('confirmAvatarMode', $event)"
  />
  <OrgAccessOrientationModals />
</template>
